import React, { useRef, useState } from "react";
import Select from "react-select";
import SimpleReactValidator from "simple-react-validator";
import Header from "../../Common/header";
import Footer from "../../Common/footer";
import StepsNavbar from './Components/stepsNavbar';
import {
  HiInformationCircle,
  HiPlus,
  HiOutlineTrash
} from "react-icons/hi";

const license = [
  { value: "CC BY-NC-SA 4.0", label: "CC BY-NC-SA 4.0" },
  { value: "CC BY-SA 4.0", label: "CC BY-SA 4.0" },
  { value: "CC BY 4.0", label: "CC BY 4.0" },
  { value: "CC BY-ND 4.0", label: "CC BY-ND 4.0" },
  { value: "CC BY-NC-ND 4.0", label: "CC BY-NC-ND 4.0" },
  { value: "OGL-Canada-2.0", label: "OGL-Canada-2.0" },
  { value: "OGL-UK-2.0", label: "OGL-UK-2.0" },
  { value: "BaySys", label: "BaySys" },
  { value: "Other (Not Open)", label: "Other (Not Open)" },
  { value: "Other (Open)", label: "Other (Open)" },
];

export default function LicensingInformation(props) {
  const projectData = props.projectData;
  const setProjectData = props.setProjectData;
  const [, forceUpdate] = useState();
  const simpleValidator = useRef(new SimpleReactValidator());

  /**
   * Checks if all fields are valid. If all fields are valid, it calls the nextPage() function to move to the next page. 
   * If there are invalid fields, it displays the validation messages for the invalid fields and scrolls to the top.
   * @param {*} e Event object
   */
  function next(e) {
    if (simpleValidator.current.allValid()) {
      props.nextPage();
    } else {
      simpleValidator.current.showMessages();
      window.scrollTo(0, 0);
      // rerender to show messages for the first time
      forceUpdate(1);
    }
  };

  /**
   * Moves the user to the previous page
   * @param {*} e Event object
   */
  function previous(e) {
    e.preventDefault();
    props.previousPage();
  };

  /**
   * Handles changes in the input fields of the form. The function updates the state object properties based on the input field that was passed into the change event.
   * @param {*} e Event object
   * @param {*} input Name of state property to update
   */
  function handleChange(e, input) {
    setProjectData((prevState) => ({
      ...prevState,
      [input]:
        typeof e.target.value === "string"
          ? e.target.value.trim()
          : e.target.value,
    }));
  };

  function handleLicense(selectedLicense) {
    setProjectData((prevState) => ({
      ...prevState,
      selectedLicense: selectedLicense.value,
    }));

    if (
      projectData.selectedLicense !== "notOpenlicenseName" ||
      projectData.selectedLicense !== "openlicenseName"
    ) {
      // Deletes custom license name from validator object to prevent the validator from trying to validate an empty custom license
      const newValidators = { ...simpleValidator.current.fields };
      delete newValidators["License Name"];
      simpleValidator.current.fields = newValidators;

      setProjectData((prevState) => ({
        ...prevState,
        notOpenlicenseName: "",
        openlicenseName: "",
      }));
    }
  };

  function handleChangeNotOpenLicenseName(e) {
    setProjectData((prevState) => ({
      ...prevState,
      notOpenlicenseName: e.target.value,
      openlicenseName: "",
    }));
  };

  function handleChangeOpenLicenseName(e) {
    setProjectData((prevState) => ({
      ...prevState,
      notOpenlicenseName: e.target.value,
      openlicenseName: "",
    }));
  };

  function handleChangeSponsor(e, index, type) {
    const { name, value } = e.target;
    const sponsorList = [...projectData.sponsorList];
    sponsorList[index][type] = value;
    setProjectData((prevState) => ({
      ...prevState,
      sponsorList: sponsorList,
    }));
  };

  // Adds a new sponsor object to sponsorList state variable
  function addNewSponsor() {
    const sponsorList = [...projectData.sponsorList];
    sponsorList.push({
      awardTitleitle: "",
      sponsor: "",
      grantNo: "",
    });
    setProjectData((prevState) => ({
      ...prevState,
      sponsorList: sponsorList,
    }));
  };

  // Removes sponsor object from sponsorList state variable
  function clickOnDeleteSponsor(index) {
    const sponsorList = [...projectData.sponsorList];
    sponsorList.splice(index, 1);
    setProjectData((prevState) => ({
      ...prevState,
      sponsorList: sponsorList,
    }));
  };

  return (
    <>
      <Header />

      <StepsNavbar currentStep={props.step} setStep={props.setStep} next={next} />

      <div className="overflow-auto dark:bg-gray-900">
      <div className="max-w-2xl m-auto mt-8 sm:mt-12 border dark:border-gray-700">
      <form>
        <div className="bg-gray-100 dark:bg-gray-800">
          <h2 className=" text-center font-bold text-gray-900 sm:text-xl sm:tracking-tight lg:text-2xl py-6 border-b-2 dark:border-gray-600 dark:text-gray-100">
            Licensing Information and Research Sponsorship Information
          </h2>

          <div className="mt-6 px-8">
            <div className="sm:col-span-6">
              <span className="mr-12">
                <h3 className="text-lg leading-6 font-semibold text-gray-900 dark:border-gray-600 dark:text-gray-100">
                  License Name
                </h3>

                <div className="">
                  <div data-cy="license" className="mt-1 text-sm text-gray-500 col-span-1 ">
                    <Select
                      className=""
                      value={{ value: projectData.selectedLicense, label: projectData.selectedLicense }}
                      onChange={handleLicense}
                      options={license}
                      defaultValue={{ value: projectData.selectedLicense, label: projectData.selectedLicense }}
                    />
                  </div>
                  <div className="lg:col-span-1">
                    <div className="rounded-md bg-blue-100 p-4 mt-4 dark:bg-gray-900">
                      <div className="flex">
                        <div className="flex-shrink-0">
                          <HiInformationCircle className="h-5 w-5 text-blue-400" aria-hidden="true" />
                        </div>
                        <div className="ml-3">
                          <div className="text-sm text-blue-600 leading-6 ">
                            {projectData.selectedLicense === "CC BY-NC-SA 4.0" ? (
                              <p>
                                This license lets others remix, tweak, and build
                                upon your work non-commercially, as long as they
                                credit you and license their new creations under the
                                identical terms.
                              </p>
                            ) : (
                              projectData.selectedLicense === "CC BY-SA 4.0" && (
                                <p>
                                  This license lets others remix, tweak, and build
                                  upon your work even for commercial purposes, as
                                  long as they credit you and license their new
                                  creations under the identical terms. This license
                                  is often compared to “copyleft” free and open
                                  source software licenses. All new works based on
                                  yours will carry the same license, so any
                                  derivatives will also allow commercial use. This
                                  is the license used by Wikipedia, and is
                                  recommended for materials that would benefit from
                                  incorporating content from Wikipedia and similarly
                                  licensed projects.
                                </p>
                              )
                            )}

                            {projectData.selectedLicense === "CC BY 4.0" ? (
                              <p>
                                This license lets others distribute, remix, tweak,
                                and build upon your work, even commercially, as long
                                as they credit you for the original creation. This
                                is the most accommodating of licenses offered.
                                Recommended for maximum dissemination and use of
                                licensed materials
                              </p>
                            ) : (
                              projectData.selectedLicense === "CC BY-ND 4.0" && (
                                <p>
                                  This license allows for redistribution, commercial
                                  and non commercial, as long as it is passed along
                                  unchanged and in whole, with credit to you.
                                </p>
                              )
                            )}

                            {projectData.selectedLicense === "CC BY-NC-ND 4.0" ? (
                              <p>
                                This license is the most restrictive of our six main
                                licenses, only allowing others to download your
                                works and share them with others as long as they
                                credit you, but they can’t change them in any way or
                                use them commercially.
                              </p>
                            ) : (
                              projectData.selectedLicense === "OGL-Canada-2.0" && (
                                <p>Government of Canada open access licence</p>
                              )
                            )}

                            {projectData.selectedLicense === "OGL-UK-2.0" ? (
                              <p>
                                Government of the United Kingdom open access licence
                              </p>
                            ) : (
                              projectData.selectedLicense === "BaySys" && (
                                <p>
                                  Licence specific to the Umanitoba BaySys project
                                </p>
                              )
                            )}

                            {projectData.selectedLicense === "Other (Not Open)" ? (
                              <>
                                <p>
                                  User provides a licence. Not open access, only
                                  metadata viewable.
                                </p>
                              </>
                            ) : (
                              projectData.selectedLicense === "Other (Open)" && (
                                <p>User provides a licence. Open access.</p>
                              )
                            )}
                          </div>
                        </div>
                      </div>
                    </div>

                    {projectData.selectedLicense === "Other (Not Open)" ? (
                      <>
                        <input
                          className="py-3 px-4 mt-2 block w-full focus:ring-1 outline-none shadow-sm focus:ring-blue-500 focus:border-blue-500 border-gray-300 border rounded-md text-gray-800"
                          type="text"
                          placeholder="Enter License Name"
                          onChange={handleChangeNotOpenLicenseName}
                          defaultValue={projectData.notOpenlicenseName}
                        />

                        {simpleValidator.current.message(
                          "License Name",
                          projectData.notOpenlicenseName,
                          "required|/^[A-Za-z]*$/",
                          {
                            className: "text-red-600 mt-2",
                          }
                        )}
                      </>
                    ) : (
                      <div></div>
                    )}

                    {projectData.selectedLicense === "Other (Open)" ? (
                      <>
                        <input
                          className="py-3 px-4 mt-2 block w-full focus:ring-1 outline-none shadow-sm focus:ring-blue-500 focus:border-blue-500 border-gray-300 border rounded-md text-gray-800"
                          type="text"
                          placeholder="Enter License name"
                          onChange={handleChangeOpenLicenseName}
                          defaultValue={projectData.openlicenseName}
                        />
                        {simpleValidator.current.message(
                          "License Name",
                          projectData.openlicenseName,
                          "required|/^[A-Za-z]*$/",
                          {
                            className: "text-red-600 mt-2",
                          }
                        )}
                      </>
                    ) : (
                      <div></div>
                    )}
                  </div>
                </div>
              </span>
            </div>
          </div>

          <div>
            <div className="px-8">
              <h3 className="text-lg font-bold leading-6 text-gray-900 mb-2 dark:border-gray-600 dark:text-gray-100">
                Funding Reference(s)
              </h3>
              <div className="mt-5  md:mt-0 md:col-span-3 ">
                <div className="shadow rounded-md bg-white border dark:border-gray-600 dark:bg-gray-700">
                  <div className="px-4 py-5 space-y-6 sm:p-6 ">
                    <div className="sm:col-span-6">
                      <label htmlFor="awardTitle" className="block text-base font-semibold text-gray-900 dark:border-gray-600 dark:text-gray-100">
                        Award Title
                      </label>
                      <div className="mt-1 text-sm text-gray-500">
                        <div className="">
                          <input
                            id="awardTitle"
                            type="text"
                            placeholder="title or name of the award"
                            data-cy="award-title"
                            onChange={(e) => handleChangeSponsor(e, 0, "awardTitle")}
                            defaultValue={projectData.sponsorList[0].awardTitle}
                            className="py-3 px-4 block w-full focus:ring-1 outline-none shadow-sm focus:ring-blue-500 focus:border-blue-500 border-gray-300 border rounded-md text-gray-800 dark:text-gray-200 dark:border-gray-600 dark:bg-gray-700"
                          />

                          {/* {projectData.sponsorList[0].awardTitle === "" ? (
                            <></>
                          ) : (
                            simpleValidator.current.message(
                              "Award Title",
                              projectData.sponsorList[0].awardTitle,
                              "required|alpha_num_dash_space",
                              {
                                className: "text-red-600  mt-2",
                              }
                            )
                          )} */}
                        </div>
                      </div>
                    </div>

                    <div className="sm:col-span-6">
                      <label htmlFor="awardSponsor" className="block text-base font-semibold text-gray-900 dark:border-gray-600 dark:text-gray-100">
                        Funder Name - Sponsor
                      </label>
                      <div className="mt-1 text-sm text-gray-500">
                        <div className="">
                          <input
                            id="awardSponsor"
                            type="text"
                            placeholder="Name of funding provider"
                            data-cy="award-sponsor"
                            onChange={(e) => handleChangeSponsor(e, 0, "sponsor")}
                            defaultValue={projectData.sponsorList[0].sponsor}
                            className="py-3 px-4 block w-full focus:ring-1 outline-none shadow-sm focus:ring-blue-500 focus:border-blue-500 border-gray-300 border rounded-md text-gray-800 dark:text-gray-200 dark:border-gray-600 dark:bg-gray-700"
                          />

                          {/* {projectData.sponsorList[0].sponsor === "" ? (
                            <></>
                          ) : (
                            simpleValidator.current.message(
                              "Funder Name - Sponsor",
                              projectData.sponsorList[0].sponsor,
                              "required|alpha_space",
                              {
                                className: "text-red-600  mt-2",
                              }
                            )
                          )} */}
                        </div>
                      </div>
                    </div>

                    <div className="sm:col-span-6">
                      <label htmlFor="grantNumber" className="block text-base font-semibold text-gray-900 dark:border-gray-600 dark:text-gray-100">
                        Grant Number
                      </label>
                      <div className="mt-1 text-sm text-gray-500">
                        <div className="">
                          <input
                            id="grantNumber"
                            placeholder="The code assigned by the funder to the grant"
                            data-cy="grant-number"
                            onChange={(e) => handleChangeSponsor(e, 0, "grantNo")}
                            defaultValue={projectData.sponsorList[0].grantNo}
                            className="py-3 px-4 block w-full focus:ring-1 outline-none shadow-sm focus:ring-blue-500 focus:border-blue-500 border-gray-300 border rounded-md text-gray-800 dark:text-gray-200 dark:border-gray-600 dark:bg-gray-700"
                          />

                          {/* {projectData.sponsorList[0].grantNo === "" ? (
                            <></>
                          ) : (
                            simpleValidator.current.message(
                              "Grant Number",
                              projectData.sponsorList[0].grantNo,
                              "required|numeric",
                              {
                                className: "text-red-600  mt-2",
                              }
                            )
                          )} */}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </form>

      {projectData.sponsorList.map((val, idx) => {
        return (
          <div key={idx} className="max-w-2xl m-auto ">
            <div className=" py-5 bg-gray-100 dark:bg-gray-800">
              {idx !== 0 && (
                <div className="px-12 ">
                  <h3 className="text-lg font-bold leading-6 text-gray-900 mb-2 dark:border-gray-600 dark:text-gray-100">
                    Funding Reference(s)
                  </h3>
                  <div className="mt-5 md:mt-0 md:col-span-3 ">
                    <div className="shadow sm:rounded-md sm:overflow-hidden ">
                      <div className="px-4 py-5 bg-white space-y-6 sm:p-6 dark:bg-gray-800 border dark:border-gray-700">
                        <div className="sm:col-span-6 ">
                          <label className="block text-base font-semibold text-gray-900 dark:border-gray-600 dark:text-gray-100">
                            Award Title
                          </label>
                          <div className="mt-1 text-sm text-gray-500 ">
                            <div className="">
                              <input
                                type="text"
                                name="title"
                                value={projectData.sponsorList[idx].awardTitle}
                                data-cy="additional-award-title"
                                onChange={(e) => handleChangeSponsor(e, idx, "awardTitle")}
                                className="py-3 px-4 block w-full focus:ring-1 outline-none shadow-sm focus:ring-blue-500 focus:border-blue-500 border-gray-300 border rounded-md text-gray-800 dark:text-gray-200 dark:border-gray-600 dark:bg-gray-700"
                              />

                              {/* {projectData.sponsorList[idx].awardTitle === "" &&
                                simpleValidator.current.message(
                                  "Award Title",
                                  projectData.sponsorList[idx].awardTitle,
                                  "required|alpha_num_dash_space",
                                  {
                                    className: "text-red-600  mt-2",
                                  }
                                )} */}
                            </div>
                          </div>
                        </div>

                        <div className="sm:col-span-6">
                          <label className="block text-base font-semibold text-gray-900 dark:border-gray-600 dark:text-gray-100">
                            Funder Name - Sponsor
                          </label>
                          <div className="mt-1 text-sm text-gray-500">
                            <div className="mt-1">
                              <input
                                type="text"
                                name="name"
                                value={projectData.sponsorList[idx].sponsor}
                                data-cy="additional-award-sponsor"
                                onChange={(e) => handleChangeSponsor(e, idx, "sponsor")}
                                className="py-3 px-4 block w-full focus:ring-1 outline-none shadow-sm focus:ring-blue-500 focus:border-blue-500 border-gray-300 border rounded-md text-gray-800 dark:text-gray-200 dark:border-gray-600 dark:bg-gray-700"
                              />

                              {/* {projectData.sponsorList[idx].sponsor === "" &&
                                simpleValidator.current.message(
                                  "Funder Name - Sponsor",
                                  projectData.sponsorList[idx].sponsor,
                                  "required|alpha_space",
                                  {
                                    className: "text-red-600  mt-2",
                                  }
                                )} */}
                            </div>
                          </div>
                        </div>

                        <div className="sm:col-span-6">
                          <label className="block text-base font-semibold text-gray-900 dark:border-gray-600 dark:text-gray-100">
                            Grant Number
                          </label>
                          <div className="mt-1 text-sm text-gray-500">
                            <div className="">
                              <input
                                type="email"
                                name="number"
                                value={projectData.sponsorList[idx].grantNo}
                                data-cy="additional-grant-number"
                                onChange={(e) => handleChangeSponsor(e, idx, "grantNo")}
                                className="py-3 px-4 block w-full focus:ring-1 outline-none shadow-sm focus:ring-blue-500 focus:border-blue-500 border-gray-300 border rounded-md text-gray-800 dark:text-gray-200 dark:border-gray-600 dark:bg-gray-700"
                              />

                              {/* {projectData.sponsorList[idx].grantNo === "" &&
                                simpleValidator.current.message(
                                  "Grant Number",
                                  projectData.sponsorList[idx].grantNo,
                                  "required|numeric",
                                  {
                                    className: "text-red-600  mt-2",
                                  }
                                )} */}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {idx !== 0 && (
                <button
                  className="flex items-center mt-4 m-auto py-2 px-4 shadow-sm text-sm font-medium rounded-md border border-1 border-red-600 hover:text-white text-red-600 hover:bg-red-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 dark:hover:bg-gray-600"
                  onClick={() => clickOnDeleteSponsor(idx)}
                >
                  <HiOutlineTrash className="w-4 h-4 mr-2" />
                  Remove Sponsor
                </button>
              )}

              {idx === 0 && (
                <button
                  onClick={addNewSponsor}
                  data-cy="add-sponsor"
                  className="flex items-center m-auto py-2 px-4 shadow-sm text-sm font-medium rounded-md border border-1 border-blue-600 hover:text-white text-blue-600 hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 dark:hover:bg-gray-600"
                >
                  <HiPlus className="w-4 h-4 mr-2" />
                  Add Sponsor
                </button>
              )}
            </div>
          </div>
        );
      })}
      </div>

      <div className="flex justify-center py-12">
        <button
          type="submit"
          className="msm:mr-8 mr-40 py-4 px-11 border rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          onClick={(e) => previous(e)}
        >
          Previous
        </button>

        <button
          type="submit"
          onClick={(e) => next(e)}
          data-cy="next"
          className="flex justify-center py-4 px-14 border rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
        >
          Next
        </button>
      </div>
      </div>

      <Footer />
    </>
  );
}